import React, { useState, useEffect } from 'react';
import { useSuggestions } from '../../contexts/SuggestionsContext';
import RadiusSelector from '../RadiusSelector';
import LandmarkCard from '../LandmarkCard';
import PersonalizedAds from '../PersonalizedAds';
import SortOptions from '../SortOptions';
import { 
  MapPin, 
  AlertCircle,
  Loader2,
  ChevronDown,
  ChevronUp,
  Database
} from 'lucide-react';

const Suggestions = () => {
  const {
    location,
    landmarks,
    ads,
    userProfile,
    loading,
    loadingMore,
    error,
    pagination,
    loadUserLocation,
    loadLandmarks,
    loadAds,
    getCacheInfo
  } = useSuggestions();
  
  // Local state for UI
  const [radius, setRadius] = useState(5);
  const [sortBy, setSortBy] = useState('distance');
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Load user location on component mount
  useEffect(() => {
    loadUserLocation();
  }, [loadUserLocation]); // eslint-disable-line react-hooks/exhaustive-deps

  // Load landmarks when location, radius, or sort changes
  useEffect(() => {
    if (location) {
      loadLandmarks({ radius, sortBy }, true);
      loadAds();
    }
  }, [location, radius, sortBy]); // eslint-disable-line react-hooks/exhaustive-deps


  const handleLoadMore = () => {
    if (!loadingMore && pagination.hasMore) {
      loadLandmarks({ radius, sortBy, page: pagination.page + 1 });
    }
  };


  if (loading && !location) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600 mx-auto mb-4" />
          <p className="text-gray-600">Getting your location...</p>
        </div>
      </div>
    );
  }

  if (error && !location) {
    return (
      <div className="space-y-6">
        <div className="bg-red-50 rounded-lg p-6">
          <div className="flex items-center space-x-3">
            <AlertCircle className="w-6 h-6 text-red-600" />
            <div>
              <h3 className="text-lg font-semibold text-red-900">Location Required</h3>
              <p className="text-red-700 mt-1">{error}</p>
              <button
                onClick={() => window.location.reload()}
                className="mt-3 bg-red-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-red-700 transition-colors flex items-center space-x-2"
              >
                <span>Try Again</span>
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Suggestions</h1>
        <p className="text-gray-600 mt-1">Discover new places and landmarks near you</p>
      </div>
      
      <div className="space-y-6">
        {/* Landmarks Section */}
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center space-x-3">
              <h3 className="text-lg font-semibold text-gray-900">
                The adventure continues
              </h3>
              {(() => {
                const cacheInfo = getCacheInfo();
                if (cacheInfo && !cacheInfo.isExpired) {
                  return (
                    <div className="flex items-center space-x-1 text-xs text-gray-500 bg-gray-100 px-2 py-1 rounded-full">
                      <Database className="w-3 h-3" />
                      <span>Cached {cacheInfo.ageMinutes}m ago</span>
                    </div>
                  );
                }
                return null;
              })()}
            </div>
            <button
              onClick={() => setFiltersExpanded(!filtersExpanded)}
              className="flex items-center space-x-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
            >
              <span>Filter</span>
              {filtersExpanded ? (
                <ChevronUp className="w-4 h-4" />
              ) : (
                <ChevronDown className="w-4 h-4" />
              )}
            </button>
          </div>

          {/* Filters */}
          {filtersExpanded && (
            <div className="mb-6 p-4 bg-gray-50 rounded-lg">
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                <div className="lg:col-span-2">
                  <RadiusSelector
                    radius={radius}
                    onRadiusChange={setRadius}
                    maxRadius={50}
                  />
                </div>
                <div>
                  <SortOptions
                    sortBy={sortBy}
                    onSortChange={setSortBy}
                  />
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-6 h-6 animate-spin text-indigo-600 mr-2" />
              <span className="text-gray-600">Loading landmarks...</span>
            </div>
          ) : landmarks.length === 0 ? (
            <div className="text-center py-8">
              <MapPin className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h4 className="text-lg font-medium text-gray-900 mb-2">No landmarks found</h4>
              <p className="text-gray-600">
                Try increasing your search radius or check back later for new suggestions.
              </p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {landmarks.map((landmark) => (
                  <LandmarkCard
                    key={landmark.id}
                    landmark={landmark}
                  />
                ))}
              </div>

              {/* Load More Button */}
              {pagination.hasMore && (
                <div className="mt-6 text-center">
                  <button
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                    className="bg-indigo-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2 mx-auto"
                  >
                    {loadingMore ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        <span>Loading...</span>
                      </>
                    ) : (
                      <span>Load More</span>
                    )}
                  </button>
                </div>
              )}
            </>
          )}
        </div>

        {/* Personalized Ads Section */}
        <PersonalizedAds ads={ads} userProfile={userProfile} />
      </div>
    </div>
  );
};

export default Suggestions;
